import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Leaf, Star, MapPin, Clock, ShieldAlert, Store, Package, Plus, Minus, Ban, MessageSquareLock, CheckCircle2, Truck, XCircle, ChevronLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* GreenPages — the free anon. flower network (LeafedOut parody). Browse NPC dealer
   menus with ratings and reviews, order for physical drop-off, or open your own
   shop and build a rating selling what you grow. Off-menu items (pills, powder)
   only move over Ghostwire. */

const fmt$ = (n) => `$${(n ?? 0).toLocaleString()}`;
const CAT_COLOR = { flower: "#4ade80", hard: "#f87171", party: "#c084fc", pills: "#60a5fa", other: "#a1a1aa" };

export const LEAF_DEMO = {
  vendors: [
    { id: "NPC-greenthumb", name: "GreenThumb Gaz", suburb: "Route 68", rating: 4.8, rating_count: 71, sales: 213, bio: "Organic living-soil flower. Lab tested, no PGR.", menu: [
      { id: "m1", label: "Violet Static", category: "flower", unit: "g", price: 15, stock: 24, off_menu: false },
      { id: "m2", label: "Whiteout", category: "flower", unit: "g", price: 19, stock: 11, off_menu: false }],
      reviews: [
        { by: "BornwoodBuyer", stars: 5, text: "Cleanest haze on the board, proper cure. Meet was on time to the minute.", at: "2026-09-12T20:11:00" },
        { by: "RegularRay", stars: 5, text: "Third reorder. Gaz answers fast and the gear is always exactly what the menu says.", at: "2026-09-10T18:44:00" },
        { by: "M", stars: 4, text: "Quality is unreal, only knock is he sells out by the weekend.", at: "2026-09-06T22:02:00" }] },
    { id: "NPC-partypack", name: "PartyPack Pete", suburb: "Mall District", rating: 4.3, rating_count: 129, sales: 389, bio: "Festival season never ends.", menu: [
      { id: "m3", label: "Sparkle (party cap)", category: "party", unit: "cap", price: 45, stock: 30, off_menu: true },
      { id: "m4", label: "Study Buddies", category: "pills", unit: "pill", price: 12, stock: 80, off_menu: true }] },
    { id: "NPC-gh0st", name: "GH0ST", suburb: "The Docks", rating: 3.2, rating_count: 29, sales: 88, wire_only: true, bio: "Everything. Cheapest on the board. Wire only.", menu: [
      { id: "m5", label: "Frostbite (shard)", category: "hard", unit: "g", price: 80, stock: 20, off_menu: true },
      { id: "m6", label: "Whiteout", category: "flower", unit: "g", price: 13, stock: 40, off_menu: false }] },
  ],
  orders: [
    { id: "demo-ord1", vendor_name: "GreenThumb Gaz", side: "buy", status: "meet_proposed", total: 210, channel: "ghostwire",
      items: [{ label: "Violet Static", qty: 14, unit: "g", price: 15 }],
      meet: { label: "Route 68 — abandoned servo", window_start: "2026-09-18T22:15:00", window_end: "2026-09-18T22:35:00" } },
    { id: "demo-ord2", vendor_name: "You", side: "sell", wholesale: true, buyer_name: "Mr. Zhou", status: "meet_proposed", total: 2419.2, channel: "ghostwire",
      items: [{ label: "Old Harbour", qty: 56, unit: "g", price: 36 }],
      meet: { label: "The Docks — container row C", window_start: "2026-09-18T23:10:00", window_end: "2026-09-18T23:30:00" } },
  ],
  blacklist: [{ id: "NPC-rippa", name: "QuickFlip Ken", suburb: "Ledger Lane", rating: 1.2, rating_count: 44, sales: 61, bio: "Took the money, never showed. Three reports in a week." }],
  inventory: [{ key: "weed|purple_haze|exotic", strain_name: "Violet Static", quality: "exotic", grams: 44, street_price_g: 28.8 }],
  my_vendor: null,
  turfs: {
    spots: [
      { id: "corner-legion", label: "Founders Square — west corner", owner: "BigSmoke", mine: false, contest: null, defended_until: "" },
      { id: "corner-docks", label: "The Docks — gate 3", owner: null, mine: false, contest: null, defended_until: "" },
      { id: "corner-fleeca", label: "Ledger Lane — arcade front", owner: "BornwoodBotanist", mine: true, contest: { challenger_name: "BigSmoke", ends_at: "2026-09-18T23:05:00", seconds_left: 1740 }, defended_until: "" },
    ],
    mine: { spot: { label: "Ledger Lane — arcade front" }, accrued: 420, rep_pending: 0, since: "2026-09-15T10:00:00",
            hit_until: "", contest: { challenger_name: "BigSmoke", ends_at: "2026-09-18T23:05:00", seconds_left: 1740 } },
    lost: { spot_label: "Founders Square — west corner", usurper_name: "BigSmoke", cost: 3000, hours_left: 19.5 },
    eligible: false,
    requirements: { rating: 4.0, sales: 5, cost: 5000, income_hr: 140, lose_below: 3.5 },
  },
  tutorial: { install_greenpages: true, install_ghostwire: false, open_shop: false, first_listing: false, first_sale: false },
};

const Stars = ({ value }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`w-2.5 h-2.5 ${s <= Math.round(value) ? "fill-amber-300 text-amber-300" : "text-white/20"}`} />)}
  </span>
);

export default function GreenPagesApp({ demo, serverId, token, accent, sfx, onMessageVendor }) {
  const [tab, setTab] = useState("browse");
  const [profile, setProfile] = useState(null); // vendor object open in the profile sheet
  const [revStars, setRevStars] = useState(0);
  const [revText, setRevText] = useState("");
  const [vendors, setVendors] = useState(demo ? LEAF_DEMO.vendors : []);
  const [orders, setOrders] = useState(demo ? LEAF_DEMO.orders : []);
  const [blacklist, setBlacklist] = useState(demo ? LEAF_DEMO.blacklist : []);
  const [myVendor, setMyVendor] = useState(demo ? LEAF_DEMO.my_vendor : null);
  const [turfs, setTurfs] = useState(demo ? LEAF_DEMO.turfs : null);
  const [inventory, setInventory] = useState(demo ? LEAF_DEMO.inventory : []);
  const [expanded, setExpanded] = useState(null);
  const [cart, setCart] = useState({}); // menu_id -> qty (per expanded vendor)
  const [handle, setHandle] = useState("");
  const [listKey, setListKey] = useState("");
  const [listPrice, setListPrice] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/leaf/state`, { params: { token } })
      .then((r) => setMyVendor(r.data.my_vendor)).catch(() => {});
    api.get(`/nc/servers/${serverId}/leaf/vendors`, { params: { token } })
      .then((r) => setVendors(r.data.vendors)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/nc/servers/${serverId}/leaf/orders`, { params: { token } })
      .then((r) => setOrders(r.data.orders)).catch(() => {});
    api.get(`/nc/servers/${serverId}/leaf/blacklist`, { params: { token } })
      .then((r) => setBlacklist(r.data.blacklist)).catch(() => {});
    api.get(`/nc/servers/${serverId}/leaf/turfs`, { params: { token } })
      .then((r) => setTurfs(r.data)).catch(() => {});
    api.get(`/nc/servers/${serverId}/drugs/state`, { params: { token } })
      .then((r) => setInventory(r.data.inventory || [])).catch(() => {});
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => {
    if (busy) return;
    sfx?.keyTick();
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy("");
    if (!demo) load();
  };

  const placeOrder = (vendor) => run(`order-${vendor.id}`, async () => {
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([menu_id, qty]) => ({ menu_id, qty }));
    if (!items.length) { toast.error("Pick something off the menu first"); return; }
    if (demo) { toast.success(`Order in with ${vendor.name} (demo) — meet proposal landed on Ghostwire`); setCart({}); setExpanded(null); return; }
    const r = await api.post(`/nc/servers/${serverId}/leaf/orders`, { vendor_id: vendor.id, items, channel: "ghostwire" }, { params: { token } });
    toast.success(r.data.message);
    setCart({}); setExpanded(null);
  });

  const step = (order, action) => run(`${action}-${order.id}`, async () => {
    if (demo) {
      if (action === "accept_meet") { setOrders((os) => os.map((o) => o.id === order.id ? { ...o, status: "scheduled" } : o)); toast.success("Meet locked (demo)"); }
      else if (action === "complete") { setOrders((os) => os.map((o) => o.id === order.id ? { ...o, status: "completed" } : o)); toast.success("Drop complete (demo) — leave a review"); }
      else toast.success(`${action} (demo)`);
      return;
    }
    const r = await api.post(`/nc/servers/${serverId}/leaf/orders/${order.id}/step`, { action }, { params: { token } });
    if (r.data.ripped) toast.error(r.data.message); else toast.success(r.data.message);
  });

  const review = (order, stars, text = "") => run(`rev-${order.id}`, async () => {
    if (demo) { toast.success(`${stars}★ posted (demo)`); setRevStars(0); setRevText(""); return; }
    const r = await api.post(`/nc/servers/${serverId}/leaf/orders/${order.id}/review`, { stars, text }, { params: { token } });
    toast.success(r.data.message);
    setRevStars(0); setRevText("");
  });

  const openShop = () => run("open-shop", async () => {
    if (handle.trim().length < 3) { toast.error("Pick a handle — 3+ characters"); return; }
    if (demo) { setMyVendor({ id: "VND-demo", name: handle, suburb: "Bornwood Central", rating: 0, rating_count: 0, sales: 0, menu: [], reviews: [] }); toast.success("You're on the board (demo)"); return; }
    const r = await api.post(`/nc/servers/${serverId}/leaf/shop/open`, { handle }, { params: { token } });
    toast.success(r.data.message);
  });

  const listItem = () => run("list", async () => {
    if (!listKey || !Number(listPrice)) { toast.error("Pick product and a price per gram"); return; }
    if (demo) { toast.success("Listed (demo)"); return; }
    const r = await api.post(`/nc/servers/${serverId}/leaf/shop/list`, { inv_key: listKey, grams: inventory.find((i) => i.key === listKey)?.grams || 0, price: Number(listPrice) }, { params: { token } });
    toast.success(r.data.message);
    setListKey(""); setListPrice("");
  });

  const delist = (itemId) => run(`delist-${itemId}`, async () => {
    if (demo) { toast.success("Listing pulled (demo)"); return; }
    const r = await api.post(`/nc/servers/${serverId}/leaf/shop/delist/${itemId}`, {}, { params: { token } });
    toast.success(r.data.message);
  });

  const claimTurf = (spotId) => run(`turf-${spotId}`, async () => {
    if (demo) { toast.success("Corner claimed (demo) — the block works for you now"); return; }
    const r = await api.post(`/nc/servers/${serverId}/leaf/turfs/${spotId}/claim`, {}, { params: { token } });
    toast.success(r.data.message);
  });

  const collectTurf = () => run("turf-collect", async () => {
    if (demo) { setTurfs((t) => ({ ...t, mine: { ...t.mine, accrued: 0 } })); toast.success("Collected $420 off the corner (demo)"); return; }
    const r = await api.post(`/nc/servers/${serverId}/leaf/turfs/collect`, {}, { params: { token } });
    toast.success(r.data.message);
  });

  const contestTurf = (spotId) => run(`contest-${spotId}`, async () => {
    if (demo) { toast.success("You've made the move (demo) — they have 30 minutes to show face"); return; }
    const r = await api.post(`/nc/servers/${serverId}/leaf/turfs/${spotId}/challenge`, {}, { params: { token } });
    toast.success(r.data.message);
  });

  const defendTurf = () => run("turf-defend", async () => {
    if (demo) {
      setTurfs((t) => ({ ...t, mine: { ...t.mine, contest: null },
        spots: t.spots.map((s) => (s.mine ? { ...s, contest: null, defended_until: "tomorrow" } : s)) }));
      toast.success("You showed face — corner holds, half their stake is yours (demo)");
      return;
    }
    const r = await api.post(`/nc/servers/${serverId}/leaf/turfs/defend`, {}, { params: { token } });
    toast.success(r.data.message);
  });

  const retaliate = () => run("turf-retaliate", async () => {
    if (demo) {
      setTurfs((t) => ({ ...t, lost: null }));
      toast.success("The crew handled it (demo) — their corner's cold for 24h");
      return;
    }
    const r = await api.post(`/nc/servers/${serverId}/leaf/turfs/retaliate`, {}, { params: { token } });
    toast.success(r.data.message);
  });

  const crewToggle = (hired) => run(`crew-${hired}`, async () => {
    if (demo) {
      setTurfs((t) => ({ ...t, mine: { ...t.mine, crew: hired } }));
      toast.success(hired ? "Crew hired (demo) — the corner watches itself" : "Crew let go (demo)");
      return;
    }
    const r = await api.post(`/nc/servers/${serverId}/leaf/turfs/crew/${hired ? "hire" : "fire"}`, {}, { params: { token } });
    toast.success(r.data.message);
  });

  const TABS = [["browse", Leaf, "Browse"], ["shop", Store, "My Shop"], ["orders", Truck, `Orders${orders.filter((o) => ["pending", "meet_proposed", "scheduled"].includes(o.status)).length ? ` (${orders.filter((o) => ["pending", "meet_proposed", "scheduled"].includes(o.status)).length})` : ""}`], ["blacklist", Ban, "Blacklist"]];

  return (
    <div data-testid="greenpages-app" className="flex flex-col min-h-full">
      <div className="px-3 pt-3 pb-2 flex items-center gap-2">
        <Leaf className="w-4 h-4 text-emerald-400" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">GreenPages</p>
        <p className="ml-auto font-mono text-[7px] text-white/30 uppercase tracking-[0.15em]">anon · no sign-up id</p>
      </div>
      <div className="flex gap-1 px-3 pb-2">
        {TABS.map(([id, Icon, lbl]) => (
          <button key={id} data-testid={`leaf-tab-${id}`} onClick={() => { setTab(id); sfx?.keyTick(); }}
            className={`flex-1 py-1.5 rounded-full font-mono text-[8px] uppercase tracking-[0.1em] flex items-center justify-center gap-1 transition-colors ${tab === id ? "bg-emerald-400 text-black font-bold" : "bg-white/5 text-white/50 hover:text-white/80"}`}>
            <Icon className="w-3 h-3" />{lbl}
          </button>
        ))}
      </div>

      <div className="flex-1 px-3 pb-3 flex flex-col gap-2.5">
        {/* vendor profile sheet — full menu, every review, leave-your-own, jump to Ghostwire */}
        {profile && (
          <div data-testid="leaf-profile" className="flex flex-col gap-2.5">
            <button data-testid="leaf-profile-back" onClick={() => setProfile(null)}
              className="self-start font-mono text-[9px] uppercase tracking-[0.15em] text-white/50 hover:text-white flex items-center gap-1">
              <ChevronLeft className="w-3.5 h-3.5" /> Board
            </button>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center"><Leaf className="w-5 h-5 text-emerald-300" /></span>
                <div className="flex-1">
                  <p className="font-sans font-bold text-base text-white flex items-center gap-1.5">{profile.name} {profile.wire_only && <MessageSquareLock className="w-3.5 h-3.5 text-sky-300" />}</p>
                  <p className="font-mono text-[8px] text-white/40 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{profile.suburb} · {profile.sales} sales</p>
                  <div className="flex items-center gap-1.5 mt-0.5"><Stars value={profile.rating} /><span className="font-mono text-[8px] text-white/40">{profile.rating} · {profile.rating_count} reviews</span></div>
                </div>
              </div>
              <p className="font-mono text-[9px] text-white/50 mt-2.5 italic">"{profile.bio}"</p>
              <button data-testid="leaf-profile-message" onClick={() => { sfx?.keyTick(); onMessageVendor?.(profile.id); }}
                className="mt-3 w-full py-2.5 rounded-xl font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-black flex items-center justify-center gap-1.5" style={{ backgroundColor: "#38bdf8" }}>
                <MessageSquareLock className="w-3.5 h-3.5" /> Message on Ghostwire
              </button>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50 mb-2">Menu</p>
              {profile.menu.map((m) => (
                <div key={m.id} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: CAT_COLOR[m.category] || "#a1a1aa" }} />
                  <p className="flex-1 font-mono text-[10px] text-white">{m.label} {m.off_menu && <span className="text-red-400 text-[7px] uppercase">· off-menu</span>}</p>
                  <p className="font-mono text-[9px] text-white/50">{fmt$(m.price)}/{m.unit} · {m.stock} left</p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50 mb-2">Reviews ({profile.reviews?.length || 0})</p>
              {(profile.reviews || []).length === 0 && <p className="font-mono text-[9px] text-white/30">No reviews yet — be the first after a drop</p>}
              {(profile.reviews || []).slice().reverse().map((r, i) => (
                <div key={i} className="py-1.5 border-b border-white/5 last:border-0">
                  <div className="flex items-center gap-1.5">
                    <Stars value={r.stars} />
                    <span className="font-mono text-[8px] text-white/40">{r.by}</span>
                    <span className="ml-auto font-mono text-[7px] text-white/25">{String(r.at || "").slice(0, 10)}</span>
                  </div>
                  {r.text && <p className="font-mono text-[9px] text-white/60 mt-0.5">{r.text}</p>}
                </div>
              ))}
              {(() => {
                const eligible = orders.find((o) => o.side === "buy" && o.vendor_id === profile.id && o.status === "completed" && !o.review);
                if (!eligible) return null;
                return (
                  <div data-testid="leaf-review-form" className="mt-2.5 border-t border-white/10 pt-2.5">
                    <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-emerald-300 mb-1.5">You had a drop with them — leave a review</p>
                    <div className="flex items-center gap-1 mb-1.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button key={s} data-testid={`leaf-profile-star-${s}`} onClick={() => setRevStars(s)}>
                          <Star className={`w-4 h-4 ${s <= revStars ? "fill-amber-300 text-amber-300" : "text-white/25"}`} />
                        </button>
                      ))}
                    </div>
                    <textarea data-testid="leaf-profile-review-text" rows={2} value={revText} onChange={(e) => setRevText(e.target.value)} maxLength={300}
                      placeholder="How was the gear, the meet, the comms?"
                      className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 font-mono text-[10px] text-white focus:outline-none focus:border-emerald-400/50 resize-none" />
                    <button data-testid="leaf-profile-review-submit" onClick={() => revStars && review(eligible, revStars, revText)} disabled={!revStars || !!busy}
                      className="mt-1.5 w-full py-2 rounded-xl font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-black disabled:opacity-40" style={{ backgroundColor: "#4ade80" }}>
                      Post review
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        {!profile && tab === "browse" && vendors.map((v) => (
          <div key={v.id} data-testid={`leaf-vendor-${v.id.slice(0, 10)}`} className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
            <button data-testid={`leaf-profile-open-${v.id.slice(0, 10)}`} onClick={() => { setProfile(v); sfx?.keyTick(); }}
              className="w-full flex items-center gap-2.5 text-left hover:opacity-80 transition-opacity">
              <span className="w-9 h-9 rounded-full bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center"><Leaf className="w-4 h-4 text-emerald-300" /></span>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-sm text-white flex items-center gap-1.5">{v.name} {v.wire_only && <MessageSquareLock className="w-3 h-3 text-sky-300" />}</p>
                <p className="font-mono text-[8px] text-white/40 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{v.suburb} · {v.sales} sales</p>
              </div>
              <div className="text-right">
                <Stars value={v.rating} />
                <p className="font-mono text-[8px] text-white/40 mt-0.5">{v.rating} ({v.rating_count})</p>
              </div>
            </button>
            <p className="font-mono text-[9px] text-white/50 mt-2 italic">"{v.bio}"</p>
            {expanded === v.id ? (
              <div className="mt-2.5 border-t border-white/10 pt-2.5 flex flex-col gap-1.5">
                {v.menu.map((m) => (
                  <div key={m.id} data-testid={`leaf-menu-${m.id}`} className="flex items-center gap-2 bg-black/30 rounded-xl px-2.5 py-2">
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: CAT_COLOR[m.category] || "#a1a1aa" }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[10px] text-white">{m.label} {m.off_menu && <span className="text-red-400 text-[7px] uppercase">· off-menu</span>}</p>
                      <p className="font-mono text-[8px] text-white/40">{fmt$(m.price)}/{m.unit} · {m.stock}{m.unit} left</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`leaf-minus-${m.id}`} onClick={() => setCart((c) => ({ ...c, [m.id]: Math.max(0, (c[m.id] || 0) - 1) }))} className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center"><Minus className="w-3 h-3 text-white/70" /></button>
                      <span className="font-mono text-[10px] text-white w-6 text-center">{cart[m.id] || 0}</span>
                      <button data-testid={`leaf-plus-${m.id}`} onClick={() => setCart((c) => ({ ...c, [m.id]: Math.min(m.stock, (c[m.id] || 0) + 1) }))} className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center"><Plus className="w-3 h-3 text-white/70" /></button>
                    </div>
                  </div>
                ))}
                {v.menu.some((m) => m.off_menu) && (
                  <p className="font-mono text-[7px] text-amber-300/80 leading-relaxed flex items-center gap-1"><ShieldAlert className="w-3 h-3 shrink-0" /> Off-menu items only move over Ghostwire — the board doesn't touch them</p>
                )}
                <div className="flex gap-1.5 mt-1">
                  <button data-testid={`leaf-order-${v.id.slice(0, 10)}`} onClick={() => placeOrder(v)} disabled={!!busy}
                    className="flex-1 py-2 rounded-xl font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-black disabled:opacity-40" style={{ backgroundColor: "#4ade80" }}>
                    Order · {fmt$(Object.entries(cart).reduce((t, [mid, q]) => t + (v.menu.find((m) => m.id === mid)?.price || 0) * q, 0))}
                  </button>
                  <button onClick={() => setExpanded(null)} className="px-3 rounded-xl bg-white/5 text-white/50"><ChevronLeft className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <button data-testid={`leaf-expand-${v.id.slice(0, 10)}`} onClick={() => { setExpanded(v.id); setCart({}); sfx?.keyTick(); }}
                className="mt-2.5 w-full py-1.5 rounded-xl bg-white/5 hover:bg-white/10 font-mono text-[8px] uppercase tracking-[0.2em] text-white/60 transition-colors">
                View menu · {v.menu.length} items
              </button>
            )}
            {v.reviews?.length > 0 && expanded === v.id && (
              <div className="mt-2 border-t border-white/5 pt-2">
                {v.reviews.slice(-2).map((r, i) => (
                  <p key={i} className="font-mono text-[8px] text-white/40 mt-1">{"★".repeat(r.stars)} — {r.text} <span className="text-white/25">· {r.by}</span></p>
                ))}
              </div>
            )}
          </div>
        ))}

        {!profile && tab === "shop" && (!myVendor ? (
          <div data-testid="leaf-shop-setup" className="rounded-2xl bg-white/5 border border-white/10 p-4">
            <Store className="w-6 h-6 text-emerald-400 mb-2" />
            <p className="font-sans font-semibold text-sm text-white">Open your shop</p>
            <p className="font-mono text-[9px] text-white/50 mt-1 leading-relaxed">List what you grow. Buyers find you on the board, you organise the drop on Ghostwire, ratings do the marketing. Grams list into escrow — delist any time.</p>
            <input data-testid="leaf-handle-input" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="dealer handle (e.g. BornwoodBotanist)"
              className="mt-3 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2.5 font-mono text-[11px] text-white focus:outline-none focus:border-emerald-400/50" />
            <button data-testid="leaf-open-shop" onClick={openShop} disabled={!!busy}
              className="mt-2 w-full py-2.5 rounded-xl font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-40" style={{ backgroundColor: "#4ade80" }}>Open shop — free</button>
          </div>
        ) : (
          <div data-testid="leaf-shop" className="flex flex-col gap-2.5">
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
              <div className="flex items-center gap-2">
                <p className="font-sans font-semibold text-sm text-white">{myVendor.name}</p>
                <Stars value={myVendor.rating} />
                <p className="ml-auto font-mono text-[8px] text-white/40">{myVendor.sales} sales · {myVendor.rating_count} reviews</p>
              </div>
              {myVendor.reviews?.slice(-3).map((r, i) => (
                <p key={i} className="font-mono text-[8px] text-white/40 mt-1.5">{"★".repeat(r.stars)} — {r.text}</p>
              ))}
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50 mb-2">List from your stash</p>
              <select data-testid="leaf-list-product" value={listKey} onChange={(e) => setListKey(e.target.value)}
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 font-mono text-[10px] text-white focus:outline-none mb-1.5">
                <option value="">— pick product —</option>
                {inventory.map((i) => <option key={i.key} value={i.key}>{i.grams}g {i.strain_name} ({i.quality}) · street {fmt$(i.street_price_g)}/g</option>)}
              </select>
              <div className="flex gap-1.5">
                <input data-testid="leaf-list-price" type="number" min="1" value={listPrice} onChange={(e) => setListPrice(e.target.value)} placeholder="$/g"
                  className="flex-1 bg-black/40 border border-white/15 rounded-xl px-3 py-2 font-mono text-[10px] text-white focus:outline-none" />
                <button data-testid="leaf-list-submit" onClick={listItem} disabled={!!busy}
                  className="px-4 rounded-xl font-mono text-[9px] font-bold uppercase text-black disabled:opacity-40" style={{ backgroundColor: "#4ade80" }}>List all</button>
              </div>
            </div>
            {myVendor.menu?.map((m) => (
              <div key={m.id} data-testid={`leaf-mylisting-${m.id}`} className="rounded-xl bg-black/30 border border-white/10 px-3 py-2 flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-emerald-300" />
                <p className="flex-1 font-mono text-[10px] text-white">{m.label} · {m.stock}{m.unit} @ {fmt$(m.price)}</p>
                <button data-testid={`leaf-delist-${m.id}`} onClick={() => delist(m.id)} className="font-mono text-[8px] uppercase text-red-400 hover:text-red-300">Pull</button>
              </div>
            ))}

            {/* corner turf — passive income for proven dealers */}
            {turfs && (
              <div data-testid="leaf-turfs" className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
                <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-white/50 mb-2 flex items-center gap-1.5"><MapPin className="w-3 h-3 text-amber-300" /> Corner turf</p>
                {turfs.lost && (
                  <div data-testid="leaf-turf-lost" className="mb-2.5 rounded-xl border border-red-400/40 bg-red-500/8 p-3">
                    <p className="font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-red-300">They took {turfs.lost.spot_label}</p>
                    <p className="font-mono text-[8px] text-white/50 mt-1 leading-relaxed">
                      {turfs.lost.usurper_name} holds it now. One drive-by per corner — theirs works at 15% for 24h. Window closes in {turfs.lost.hours_left}h.
                    </p>
                    <button data-testid="leaf-turf-retaliate" onClick={retaliate} disabled={!!busy}
                      className="mt-2 w-full py-2 rounded-xl font-mono text-[9px] font-bold uppercase tracking-[0.15em] border border-red-400/60 text-red-300 hover:bg-red-500/15 disabled:opacity-40 transition-colors">
                      Send the crew — {fmt$(turfs.lost.cost)}
                    </button>
                  </div>
                )}
                {turfs.mine ? (
                  <>
                    {turfs.mine.contest && (
                      <div data-testid="leaf-turf-attack" className="mb-2.5 rounded-xl border border-red-400/50 bg-red-500/10 p-3 animate-pulse">
                        <p className="font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-red-300 flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" /> Under attack</p>
                        <p className="font-mono text-[8px] text-red-200/80 mt-1 leading-relaxed">
                          {turfs.mine.contest.challenger_name} is moving on your corner — show face within{" "}
                          <span className="text-red-300 font-bold">{Math.floor(turfs.mine.contest.seconds_left / 60)}m {turfs.mine.contest.seconds_left % 60}s</span> or lose it
                        </p>
                        <button data-testid="leaf-turf-defend" onClick={defendTurf} disabled={!!busy}
                          className="mt-2 w-full py-2 rounded-xl font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-black disabled:opacity-40" style={{ backgroundColor: "#f87171" }}>
                          Show face — defend the corner
                        </button>
                      </div>
                    )}
                    <p className="font-mono text-[10px] text-white">{turfs.mine.spot.label}</p>
                    {turfs.mine.hit_until && (
                      <p data-testid="leaf-turf-hot" className="font-mono text-[8px] uppercase tracking-[0.1em] text-red-300 mt-1 flex items-center gap-1">
                        <ShieldAlert className="w-3 h-3" /> Corner hot — take at 15% until {String(turfs.mine.hit_until).slice(11, 16)}
                      </p>
                    )}
                    <p className="font-mono text-[8px] text-white/40 mt-0.5">Corner take building: <span className="text-amber-300">{fmt$(turfs.mine.accrued)}</span>{turfs.mine.rep_pending > 0 && ` · +${turfs.mine.rep_pending} rep`}</p>
                    <button data-testid="leaf-turf-collect" onClick={collectTurf} disabled={!!busy || !turfs.mine.accrued}
                      className="mt-2 w-full py-2 rounded-xl font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-black disabled:opacity-40" style={{ backgroundColor: "#f59e0b" }}>
                      Collect the take
                    </button>
                    <button data-testid="leaf-turf-crew" onClick={() => crewToggle(!turfs.mine.crew)} disabled={!!busy}
                      className={`mt-1.5 w-full py-2 rounded-xl font-mono text-[8px] uppercase tracking-[0.15em] border transition-colors disabled:opacity-40 ${turfs.mine.crew ? "border-emerald-400/40 text-emerald-300" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                      {turfs.mine.crew ? "Crew on the corner · 15% cut · auto-defends daily — tap to fire" : "Hire corner crew — $1,000 · 15% cut · auto-defends once a day"}
                    </button>
                  </>
                ) : turfs.eligible ? (
                  <>
                    <p className="font-mono text-[8px] text-white/40 mb-2">You're proven — claim a corner for {fmt$(turfs.requirements.cost)}. It works for you around the clock.</p>
                    {turfs.spots.filter((s) => !s.owner).slice(0, 3).map((s) => (
                      <button key={s.id} data-testid={`leaf-turf-claim-${s.id}`} onClick={() => claimTurf(s.id)} disabled={!!busy}
                        className="w-full mt-1.5 rounded-xl bg-black/30 border border-white/10 px-3 py-2 flex items-center gap-2 hover:border-amber-400/40 disabled:opacity-40 transition-colors text-left">
                        <MapPin className="w-3 h-3 text-amber-300 shrink-0" />
                        <span className="flex-1 font-mono text-[9px] text-white">{s.label}</span>
                        <span className="font-mono text-[8px] uppercase text-amber-300">Claim</span>
                      </button>
                    ))}
                  </>
                ) : (
                  <p className="font-mono text-[8px] text-white/40 leading-relaxed">
                    Corners pay {fmt$(turfs.requirements.income_hr)}/hr passive — held by dealers with {turfs.requirements.rating}★+ and {turfs.requirements.sales}+ sales.
                    Drop below {turfs.requirements.lose_below}★ and the street takes it back.
                  </p>
                )}
                {/* rival corners — contest them */}
                {turfs.spots.some((s) => s.owner && !s.mine) && (
                  <div className="mt-2.5 border-t border-white/10 pt-2">
                    <p className="font-mono text-[7px] uppercase tracking-[0.2em] text-white/30 mb-1.5">Rival corners — make a move ($2,500 stake)</p>
                    {turfs.spots.filter((s) => s.owner && !s.mine).map((s) => (
                      <div key={s.id} className="flex items-center gap-2 py-1">
                        <MapPin className="w-3 h-3 text-red-400/70 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-[9px] text-white/80 truncate">{s.label}</p>
                          <p className="font-mono text-[7px] text-white/40">
                            {s.owner}{s.club_crew ? ` [${s.club_crew.tag}]` : ""}{s.contest ? ` · contested — ${Math.floor(s.contest.seconds_left / 60)}m left` : s.defended_until ? " · fortified" : ""}
                          </p>
                        </div>
                        {!s.contest && !s.defended_until && (
                          <button data-testid={`leaf-turf-contest-${s.id}`} onClick={() => contestTurf(s.id)} disabled={!!busy}
                            className="font-mono text-[8px] uppercase text-red-400 border border-red-400/40 px-2 py-1 rounded-lg hover:bg-red-500/10 disabled:opacity-40 transition-colors">
                            Contest
                          </button>
                        )}
                        {s.contest && <span className="font-mono text-[7px] uppercase text-red-300 border border-red-400/40 px-1.5 py-0.5 rounded-full">war</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {!profile && tab === "orders" && (
          <>
            {orders.length === 0 && <p data-testid="leaf-no-orders" className="font-mono text-[10px] text-white/40 text-center mt-10">No orders yet — buy off the board or open your shop</p>}
            {orders.map((o) => (
              <div key={o.id} data-testid={`leaf-order-card-${o.id.slice(0, 8)}`} className="rounded-2xl bg-white/5 border border-white/10 p-3.5">
                <div className="flex items-center gap-2">
                  <p className="font-sans font-semibold text-xs text-white">{o.side === "buy" ? `From ${o.vendor_name}` : `${o.buyer_name || "Buyer"} wants`}</p>
                  {o.wholesale && <span className="font-mono text-[7px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-full border border-purple-400/50 text-purple-300">wholesale</span>}
                  <span className={`ml-auto font-mono text-[7px] uppercase px-1.5 py-0.5 rounded-full border ${o.status === "completed" ? "border-emerald-400/40 text-emerald-300" : o.status === "ripped" || o.status === "expired" ? "border-red-400/40 text-red-400" : "border-amber-400/40 text-amber-300"}`}>{o.status.replace("_", " ")}</span>
                </div>
                <p className="font-mono text-[9px] text-white/50 mt-1">{o.items.map((i) => `${i.qty}${i.unit} ${i.label}`).join(", ")} · {fmt$(o.total)} · via {o.channel === "ghostwire" ? "Ghostwire (encrypted)" : "SMS (tappable!)"}</p>
                {["meet_proposed", "scheduled"].includes(o.status) && (
                  <div className="mt-2 rounded-xl bg-sky-400/10 border border-sky-400/25 px-3 py-2">
                    <p className="font-mono text-[9px] text-sky-200 flex items-center gap-1.5"><MapPin className="w-3 h-3" />{o.meet.label}</p>
                    <p className="font-mono text-[8px] text-sky-300/70 mt-0.5 flex items-center gap-1.5"><Clock className="w-3 h-3" />Window {String(o.meet.window_start).slice(11, 16)} – {String(o.meet.window_end).slice(11, 16)} · blip's on your map</p>
                  </div>
                )}
                <div className="flex gap-1.5 mt-2.5">
                  {o.status === "meet_proposed" && (
                    <button data-testid={`leaf-accept-${o.id.slice(0, 8)}`} onClick={() => step(o, "accept_meet")} disabled={!!busy}
                      className="flex-1 py-2 rounded-xl font-mono text-[8px] font-bold uppercase tracking-[0.15em] text-black disabled:opacity-40" style={{ backgroundColor: "#38bdf8" }}>Lock the meet</button>
                  )}
                  {o.status === "scheduled" && (
                    <button data-testid={`leaf-complete-${o.id.slice(0, 8)}`} onClick={() => step(o, "complete")} disabled={!!busy}
                      className="flex-1 py-2 rounded-xl font-mono text-[8px] font-bold uppercase tracking-[0.15em] text-black disabled:opacity-40" style={{ backgroundColor: "#4ade80" }}>Done the drop</button>
                  )}
                  {["meet_proposed", "scheduled"].includes(o.status) && (
                    <button data-testid={`leaf-cancel-${o.id.slice(0, 8)}`} onClick={() => step(o, "cancel")} disabled={!!busy}
                      className="px-3 py-2 rounded-xl font-mono text-[8px] uppercase border border-red-400/40 text-red-400 disabled:opacity-40">Bail</button>
                  )}
                  {o.status === "completed" && o.side === "buy" && !o.review && (
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <button key={s} data-testid={`leaf-review-${o.id.slice(0, 8)}-${s}`} onClick={() => review(o, s)} disabled={!!busy}>
                          <Star className="w-4 h-4 text-amber-300 hover:fill-amber-300 transition-colors" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {!profile && tab === "blacklist" && (
          <>
            <p className="font-mono text-[8px] text-white/40 leading-relaxed px-1">Community-reported rips. Send money to these handles at your own risk.</p>
            {blacklist.length === 0 && <p data-testid="leaf-blacklist-empty" className="font-mono text-[10px] text-white/40 text-center mt-10">Board's clean — for now</p>}
            {blacklist.map((v) => (
              <div key={v.id} data-testid={`leaf-black-${v.id.slice(0, 10)}`} className="rounded-2xl bg-red-500/5 border border-red-400/25 p-3.5">
                <div className="flex items-center gap-2">
                  <Ban className="w-4 h-4 text-red-400" />
                  <p className="font-sans font-semibold text-sm text-red-200">{v.name}</p>
                  <p className="ml-auto font-mono text-[8px] text-red-300/70">{v.rating}★ · {v.rating_count} reports</p>
                </div>
                <p className="font-mono text-[9px] text-white/50 mt-1.5 italic">"{v.bio}"</p>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
